import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
	Copy,
	Eye,
	Loader2,
	MessageCircle,
	Pencil,
	Plus,
	RefreshCw,
	Trash2,
	X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
	Button,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui';
import { DataTablePagination } from '@/components/data-table/Pagination';
import { WhatsAppMessagePreview } from '@/components/whatsapp/WhatsAppMessagePreview';
import { ConfirmActionModal } from '@/components/ui/ConfirmActionModal';
import { SessionWhatsAppCreateModal } from './SessionWhatsAppCreateModal';
import {
	useCreateSessionWhatsAppNotification,
	useDeleteSessionWhatsAppNotification,
	useGetSession,
	useGetSessionWhatsAppNotifications,
	useUpdateSessionWhatsAppNotification,
} from '@/services';
import { getSessionDisplayTitle } from '@/utils';
import { resolveSessionWhatsAppContext } from '@/utils/resolveSessionWhatsAppContext';
import { cn } from '@/lib/utils';
import {
	formatSessionWhatsAppPhone,
	formatSessionWhatsAppScheduledAt,
	getSessionWhatsAppMessageDisplayTime,
	getSessionWhatsAppReceiptLevel,
	getSessionWhatsAppTimelineEntries,
	isValidWhatsAppPhone,
	SESSION_WHATSAPP_STATUSES,
	SESSION_WHATSAPP_STATUS_LABEL,
	SESSION_WHATSAPP_STATUS_STYLES,
	SESSION_WHATSAPP_TYPE_LABEL,
	SESSION_WHATSAPP_TYPES,
} from '@/utils/sessionWhatsAppDisplay';
import { WhatsAppMessageReceipt } from '@/components/whatsapp/WhatsAppMessageReceipt';

const FILTER_ALL = '__all__';
const PAGE_SIZE = 20;
const POLLING_INTERVAL_MS = 30_000;

const SCROLL_CLASS = cn(
	'overflow-y-auto overscroll-contain scroll-smooth [scrollbar-gutter:stable]',
	'[scrollbar-width:thin] [scrollbar-color:theme(colors.zinc.300)_transparent]',
	'[&::-webkit-scrollbar]:w-1.5',
	'[&::-webkit-scrollbar-track]:bg-transparent',
	'[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-zinc-300',
	'hover:[&::-webkit-scrollbar-thumb]:bg-zinc-400',
);

const fromDateTimeLocalValue = (value: string): string => {
	if (!value) return new Date().toISOString();
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime())
		? new Date().toISOString()
		: parsed.toISOString();
};

type SessionWhatsAppModalProps = {
	isOpen: boolean;
	onClose: () => void;
	session: Sessions.Session | null;
};

const SessionWhatsAppModal = ({
	isOpen,
	onClose,
	session,
}: SessionWhatsAppModalProps) => {
	const [page, setPage] = useState(1);
	const [statusFilter, setStatusFilter] = useState(FILTER_ALL);
	const [typeFilter, setTypeFilter] = useState(FILTER_ALL);
	const [editingNotification, setEditingNotification] =
		useState<SessionWhatsApp.SessionWhatsAppNotification | null>(null);
	const [previewNotification, setPreviewNotification] =
		useState<SessionWhatsApp.SessionWhatsAppNotification | null>(null);
	const [deleteTarget, setDeleteTarget] =
		useState<SessionWhatsApp.SessionWhatsAppNotification | null>(null);
	const [isCreateOpen, setIsCreateOpen] = useState(false);

	const sessionId = session?.id ?? '';

	const { data: fetchedSession } = useGetSession(sessionId, {
		enabled: isOpen && !!sessionId,
	});

	const activeSession = fetchedSession ?? session;

	useEffect(() => {
		if (!isOpen) return;
		setPage(1);
		setStatusFilter(FILTER_ALL);
		setTypeFilter(FILTER_ALL);
		setEditingNotification(null);
		setPreviewNotification(null);
		setDeleteTarget(null);
		setIsCreateOpen(false);
	}, [isOpen, sessionId]);

	useEffect(() => {
		setPage(1);
	}, [statusFilter, typeFilter]);

	const queryParams = useMemo<SessionWhatsApp.ListParams>(() => {
		const params: SessionWhatsApp.ListParams = {
			page,
			limit: PAGE_SIZE,
			sort_direction: 'asc',
			session_id: sessionId,
		};
		if (statusFilter !== FILTER_ALL) {
			params.status =
				statusFilter as SessionWhatsApp.SessionWhatsAppNotificationStatus;
		}
		if (typeFilter !== FILTER_ALL) {
			params.type =
				typeFilter as SessionWhatsApp.SessionWhatsAppNotificationType;
		}
		return params;
	}, [page, sessionId, statusFilter, typeFilter]);

	const { data, isLoading, isError, isFetching, refetch } =
		useGetSessionWhatsAppNotifications(queryParams, {
			enabled: isOpen && !!sessionId,
			refetchInterval: isOpen ? POLLING_INTERVAL_MS : false,
		});

	const notifications = data?.data ?? [];

	const sessionContext = useMemo(
		() =>
			activeSession
				? resolveSessionWhatsAppContext(activeSession, {
						fallbackPhones: notifications.map(
							(entry) => entry.phone_snapshot,
						),
				  })
				: null,
		[activeSession, notifications],
	);

	const { mutate: updateNotification, isPending: isUpdating } =
		useUpdateSessionWhatsAppNotification(() => {
			setEditingNotification(null);
		});

	const { mutate: createNotification, isPending: isCreating } =
		useCreateSessionWhatsAppNotification(() => {
			setIsCreateOpen(false);
		});

	const { mutate: deleteNotification, isPending: isDeleting } =
		useDeleteSessionWhatsAppNotification(() => {
			setDeleteTarget(null);
			setEditingNotification(null);
		});

	const totalItems = data?.pagination?.total ?? 0;
	const sessionTitle = activeSession
		? getSessionDisplayTitle(activeSession)
		: 'Sessão';

	const handleSaveEdit = (payload: {
		type: SessionWhatsApp.SessionWhatsAppNotificationType;
		scheduledFor: string;
		phone: string;
		body: string;
		status?: SessionWhatsApp.SessionWhatsAppNotificationStatus;
		statusDescription?: string;
	}) => {
		if (!editingNotification) return;

		const phoneDigits = payload.phone.replace(/\D/g, '');
		if (!isValidWhatsAppPhone(phoneDigits)) {
			toast.error(
				'Telefone inválido para notificação WhatsApp (11 dígitos).',
			);
			return;
		}
		if (!payload.body.trim()) {
			toast.error('Informe o texto da mensagem.');
			return;
		}

		updateNotification({
			id: editingNotification.id,
			body: {
				scheduled_for: fromDateTimeLocalValue(payload.scheduledFor),
				phone_snapshot: phoneDigits,
				rendered_body: payload.body.trim(),
				status: payload.status,
				status_description: payload.statusDescription?.trim() || null,
			},
		});
	};

	const handleCreate = (payload: {
		type: SessionWhatsApp.SessionWhatsAppNotificationType;
		scheduledFor: string;
		phone: string;
		body: string;
	}) => {
		if (
			!activeSession ||
			!sessionContext?.patientId ||
			!sessionContext.professionalId
		) {
			toast.error(
				'Não foi possível identificar paciente ou profissional da sessão.',
			);
			return;
		}
		const phoneDigits = payload.phone.replace(/\D/g, '');
		if (!isValidWhatsAppPhone(phoneDigits)) {
			toast.error(
				'Telefone inválido para notificação WhatsApp (11 dígitos).',
			);
			return;
		}
		if (!payload.body.trim()) {
			toast.error('Informe o texto da mensagem.');
			return;
		}

		createNotification({
			session_id: activeSession.id,
			patient_id: sessionContext.patientId,
			professional_id: sessionContext.professionalId,
			type: payload.type,
			scheduled_for: fromDateTimeLocalValue(payload.scheduledFor),
			phone_snapshot: phoneDigits,
			rendered_body: payload.body.trim(),
		});
	};

	const handleCopyMessage = async (message: string) => {
		try {
			await navigator.clipboard.writeText(message);
			toast.success('Mensagem copiada.');
		} catch {
			toast.error('Não foi possível copiar a mensagem.');
		}
	};

	useEffect(() => {
		if (!isOpen) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose();
		};

		document.body.style.overflow = 'hidden';
		window.addEventListener('keydown', onKeyDown);

		return () => {
			document.body.style.overflow = 'auto';
			window.removeEventListener('keydown', onKeyDown);
		};
	}, [isOpen, onClose]);

	if (!isOpen || !session) return null;

	return createPortal(
		<>
			<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
				<div
					className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
					onClick={onClose}
					aria-hidden
				/>
				<div
					className="animate-in zoom-in-95 relative flex h-[75vh] w-[75vw] max-w-[75vw] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200"
					role="dialog"
					aria-modal="true"
					aria-labelledby="session-whatsapp-modal-title"
				>
					<div className="flex shrink-0 items-start justify-between gap-4 border-b border-stone-100 px-8 py-6">
						<div className="flex min-w-0 items-center gap-4">
							<div className="flex size-12 shrink-0 items-center justify-center rounded-xl border border-stone-200 bg-stone-50 text-stone-700">
								<MessageCircle
									className="size-5"
									strokeWidth={2.25}
									aria-hidden
								/>
							</div>
							<div className="min-w-0">
								<h2
									id="session-whatsapp-modal-title"
									className="font-albert truncate text-xl font-bold text-stone-900"
								>
									{sessionTitle}
								</h2>
								<p className="text-sm text-stone-500">
									Notificações WhatsApp da sessão
								</p>
							</div>
						</div>
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={() => void refetch()}
								disabled={isFetching}
								className="rounded-lg border border-stone-200 p-2 text-stone-500 transition hover:bg-stone-50 hover:text-stone-700 disabled:opacity-50"
								aria-label="Atualizar lista"
							>
								<RefreshCw
									size={16}
									className={cn(isFetching && 'animate-spin')}
								/>
							</button>
							<button
								type="button"
								onClick={onClose}
								className="shrink-0 text-stone-400 transition-colors hover:text-stone-600"
								aria-label="Fechar"
							>
								<X size={20} />
							</button>
						</div>
					</div>

					<div className="shrink-0 border-b border-stone-100 bg-stone-50/60 px-8 py-4">
						<div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
							<div className="grid flex-1 gap-3 sm:grid-cols-2">
								<div>
									<span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-stone-500">
										Status
									</span>
									<Select
										value={statusFilter}
										onValueChange={setStatusFilter}
									>
										<SelectTrigger className="h-10 w-full border-stone-200 bg-white text-left text-sm">
											<SelectValue>
												{statusFilter === FILTER_ALL
													? 'Todos'
													: SESSION_WHATSAPP_STATUS_LABEL[
															statusFilter as SessionWhatsApp.SessionWhatsAppNotificationStatus
													  ]}
											</SelectValue>
										</SelectTrigger>
										<SelectContent>
											<SelectItem value={FILTER_ALL}>
												Todos
											</SelectItem>
											{SESSION_WHATSAPP_STATUSES.map(
												(status) => (
													<SelectItem
														key={status}
														value={status}
													>
														{
															SESSION_WHATSAPP_STATUS_LABEL[
																status
															]
														}
													</SelectItem>
												),
											)}
										</SelectContent>
									</Select>
								</div>
								<div>
									<span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-stone-500">
										Tipo
									</span>
									<Select
										value={typeFilter}
										onValueChange={setTypeFilter}
									>
										<SelectTrigger className="h-10 w-full border-stone-200 bg-white text-left text-sm">
											<SelectValue>
												{typeFilter === FILTER_ALL
													? 'Todos'
													: SESSION_WHATSAPP_TYPE_LABEL[
															typeFilter as SessionWhatsApp.SessionWhatsAppNotificationType
													  ]}
											</SelectValue>
										</SelectTrigger>
										<SelectContent>
											<SelectItem value={FILTER_ALL}>
												Todos
											</SelectItem>
											{SESSION_WHATSAPP_TYPES.map(
												(type) => (
													<SelectItem
														key={type}
														value={type}
													>
														{
															SESSION_WHATSAPP_TYPE_LABEL[
																type
															]
														}
													</SelectItem>
												),
											)}
										</SelectContent>
									</Select>
								</div>
							</div>
							<Button
								type="button"
								text="Nova notificação"
								className="h-10 shrink-0 rounded-lg border border-stone-200 bg-white px-4 text-sm font-semibold text-stone-700 hover:bg-stone-50"
								startIcon={<Plus size={16} />}
								onClick={() => setIsCreateOpen(true)}
							/>
						</div>
					</div>

					<div
						className={cn('min-h-0 flex-1 px-8 py-6', SCROLL_CLASS)}
					>
						{isLoading ||
						(isFetching && notifications.length === 0) ? (
							<div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-stone-200 bg-stone-50 px-4 py-16 text-sm text-stone-500">
								<Loader2
									className="size-4 animate-spin"
									aria-hidden
								/>
								Carregando notificações…
							</div>
						) : isError ? (
							<p className="rounded-xl border border-rose-100 bg-rose-50 px-4 py-10 text-center text-sm text-rose-800">
								Não foi possível carregar as notificações
								WhatsApp.
							</p>
						) : notifications.length === 0 ? (
							<p className="rounded-xl border border-dashed border-stone-200 bg-stone-50 px-4 py-12 text-center text-sm text-stone-500">
								Nenhuma notificação encontrada para esta sessão.
							</p>
						) : (
							<ul className="space-y-3">
								{notifications.map((notification) => {
									const timelineEntries =
										getSessionWhatsAppTimelineEntries(
											notification,
										);
									const receiptLevel =
										getSessionWhatsAppReceiptLevel(
											notification,
										);
									const messageTime =
										getSessionWhatsAppMessageDisplayTime(
											notification,
										);

									return (
										<li
											key={notification.id}
											className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm"
										>
											<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
												<div className="min-w-0 space-y-2">
													<div className="flex flex-wrap items-center gap-2">
														<span className="text-sm font-semibold text-stone-900">
															{
																SESSION_WHATSAPP_TYPE_LABEL[
																	notification
																		.type
																]
															}
														</span>
														<span
															className={cn(
																'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
																SESSION_WHATSAPP_STATUS_STYLES[
																	notification
																		.status
																],
															)}
														>
															{
																SESSION_WHATSAPP_STATUS_LABEL[
																	notification
																		.status
																]
															}
														</span>
														{messageTime ? (
															<span className="inline-flex items-center gap-0.5 text-[11px] text-stone-500">
																{messageTime}
																<WhatsAppMessageReceipt
																	level={
																		receiptLevel
																	}
																/>
															</span>
														) : (
															<WhatsAppMessageReceipt
																level={
																	receiptLevel
																}
															/>
														)}
													</div>
													<div className="grid gap-1 text-xs text-stone-500 sm:grid-cols-2">
														<p>
															<span className="font-semibold text-stone-600">
																Agendada:
															</span>{' '}
															{formatSessionWhatsAppScheduledAt(
																notification.scheduled_for,
															)}
														</p>
														<p>
															<span className="font-semibold text-stone-600">
																Telefone:
															</span>{' '}
															{formatSessionWhatsAppPhone(
																notification.phone_snapshot,
															)}
														</p>
														{timelineEntries.map(
															(entry) => (
																<p
																	key={
																		entry.key
																	}
																>
																	<span className="font-semibold text-stone-600">
																		{
																			entry.label
																		}
																		:
																	</span>{' '}
																	{formatSessionWhatsAppScheduledAt(
																		entry.at,
																	)}
																</p>
															),
														)}
														{notification.status_description ? (
															<p className="sm:col-span-2">
																<span className="font-semibold text-stone-600">
																	Obs.:
																</span>{' '}
																{
																	notification.status_description
																}
															</p>
														) : null}
													</div>
													<div className="flex items-start gap-2">
														<p className="line-clamp-3 min-w-0 flex-1 whitespace-pre-wrap text-xs text-stone-400">
															{
																notification.rendered_body
															}
														</p>
														<button
															type="button"
															onClick={() =>
																void handleCopyMessage(
																	notification.rendered_body,
																)
															}
															className="shrink-0 rounded-md p-1 text-stone-400 transition hover:bg-stone-100 hover:text-stone-600"
															title="Copiar mensagem"
														>
															<Copy size={14} />
														</button>
													</div>
												</div>
												<div className="flex shrink-0 items-center gap-1">
													<button
														type="button"
														onClick={() =>
															setPreviewNotification(
																notification,
															)
														}
														className="rounded-lg p-2 text-stone-500 transition hover:bg-stone-100 hover:text-stone-800"
														title="Pré-visualizar mensagem"
													>
														<Eye size={16} />
													</button>
													<button
														type="button"
														onClick={() =>
															setEditingNotification(
																notification,
															)
														}
														className="rounded-lg p-2 text-stone-500 transition hover:bg-stone-100 hover:text-stone-800"
														title="Editar notificação"
													>
														<Pencil size={16} />
													</button>
													<button
														type="button"
														onClick={() =>
															setDeleteTarget(
																notification,
															)
														}
														className="rounded-lg p-2 text-stone-500 transition hover:bg-stone-100 hover:text-rose-600"
														title="Excluir notificação"
													>
														<Trash2 size={16} />
													</button>
												</div>
											</div>
										</li>
									);
								})}
							</ul>
						)}
					</div>

					{totalItems > PAGE_SIZE ? (
						<div className="flex shrink-0 items-center justify-between border-t border-stone-100 px-8 py-4">
							<p className="text-xs text-stone-500">
								{totalItems} notificaç
								{totalItems === 1 ? 'ão' : 'ões'}
								{isFetching ? (
									<span className="ml-2 inline-flex items-center gap-1 text-stone-400">
										<Loader2
											className="size-3 animate-spin"
											aria-hidden
										/>
										Atualizando…
									</span>
								) : null}
							</p>
							<DataTablePagination
								page={page}
								pageSize={PAGE_SIZE}
								totalItems={totalItems}
								onPageChange={setPage}
							/>
						</div>
					) : isFetching ? (
						<div className="flex shrink-0 items-center justify-end border-t border-stone-100 px-8 py-3">
							<span className="inline-flex items-center gap-1.5 text-xs text-stone-400">
								<Loader2
									className="size-3 animate-spin"
									aria-hidden
								/>
								Atualizando…
							</span>
						</div>
					) : null}
				</div>
			</div>

			<WhatsAppMessagePreview
				isOpen={previewNotification != null}
				onClose={() => setPreviewNotification(null)}
				message={previewNotification?.rendered_body ?? ''}
				patientName={sessionContext?.patientName}
				patientPhone={
					previewNotification?.phone_snapshot ?? sessionContext?.phone
				}
				notification={previewNotification}
			/>

			{activeSession && sessionContext ? (
				<>
					<SessionWhatsAppCreateModal
						isOpen={isCreateOpen}
						onClose={() => setIsCreateOpen(false)}
						session={activeSession}
						sessionContext={sessionContext}
						isSubmitting={isCreating}
						onSubmit={handleCreate}
					/>
					<SessionWhatsAppCreateModal
						isOpen={editingNotification != null}
						onClose={() => setEditingNotification(null)}
						session={activeSession}
						sessionContext={sessionContext}
						notification={editingNotification}
						isSubmitting={isUpdating}
						onSubmit={handleSaveEdit}
					/>
				</>
			) : null}

			<ConfirmActionModal
				isOpen={deleteTarget != null}
				onClose={() => setDeleteTarget(null)}
				onConfirm={() => {
					if (!deleteTarget) return;
					deleteNotification({
						id: deleteTarget.id,
						sessionId: deleteTarget.session_id,
					});
				}}
				title="Excluir notificação?"
				description="Esta ação remove o registro de notificação WhatsApp. Não é possível desfazer."
				confirmButtonText={isDeleting ? 'Excluindo…' : 'Excluir'}
				disableConfirm={isDeleting}
			/>
		</>,
		document.body,
	);
};

export { SessionWhatsAppModal };
